import React, { useState } from 'react';
import {
  Card,
  Select,
  Table,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Button,
  Spin,
  Empty
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
// Charts will be implemented when @ant-design/plots is available
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetAdminBrandsQuery,
  useGetBrandMonitoringHistoryQuery,
  useGetResponseMonitoringStatisticsQuery
} from '../../../services/nestjs-base-api';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Text } = Typography;

interface MonitoringCheck {
  id: number;
  periodStart: string;
  periodEnd: string;
  responseCount: number;
  thresholdLevel: 'critical' | 'warning' | 'low' | 'acceptable';
  messageGenerated: boolean;
  messageSent: boolean;
  createdAt: string;
}

export const MonitoringHistoryTab: React.FC = () => {
  const { user } = useAuth();
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  const { data: brandsResponse } = useGetAdminBrandsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const brands = brandsResponse?.data || [];

  const { data: brandHistory, isLoading: historyLoading, refetch } = useGetBrandMonitoringHistoryQuery({
    jwtToken: user?.jwtToken || '',
    brandId: selectedBrandId!,
  }, { skip: !selectedBrandId });

  const { data: statistics } = useGetResponseMonitoringStatisticsQuery({
    jwtToken: user?.jwtToken || '',
  });

  const getThresholdTag = (level: string) => {
    const config = {
      critical: { color: 'red', text: 'Critical' },
      warning: { color: 'orange', text: 'Warning' },
      low: { color: 'blue', text: 'Low' },
      acceptable: { color: 'green', text: 'Acceptable' },
    };
    const { color, text } = config[level as keyof typeof config] || config.acceptable;
    return <Tag color={color}>{text}</Tag>;
  };

  const columns: ColumnsType<MonitoringCheck> = [
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <div>
          <div>{new Date(record.periodStart).toLocaleDateString()}</div>
          <Text type="secondary">to {new Date(record.periodEnd).toLocaleDateString()}</Text>
        </div>
      ),
    },
    {
      title: 'Response Count',
      dataIndex: 'responseCount',
      key: 'responseCount',
      render: (count: number) => (
        <span style={{ fontWeight: 500, fontSize: 16 }}>{count}</span>
      ),
    },
    {
      title: 'Threshold Level',
      dataIndex: 'thresholdLevel',
      key: 'thresholdLevel',
      render: (level: string) => getThresholdTag(level),
    },
    {
      title: 'Message Status',
      key: 'messageStatus',
      render: (_, record) => {
        if (!record.messageGenerated) {
          return <Tag color="default">No Message</Tag>;
        }
        return record.messageSent
          ? <Tag color="green">Sent</Tag>
          : <Tag color="orange">Generated</Tag>;
      },
    },
    {
      title: 'Check Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  // Prepare chart data
  const chartData = brandHistory?.map((check: MonitoringCheck, index: number) => ({
    period: `Period ${index + 1}`,
    date: new Date(check.periodEnd).toLocaleDateString(),
    responses: check.responseCount,
    level: check.thresholdLevel,
  })) || [];

  const lineConfig = {
    data: chartData,
    xField: 'date',
    yField: 'responses',
    point: {
      size: 5,
      shape: 'diamond',
      style: {
        fill: 'white',
        stroke: '#2593fc',
        lineWidth: 2,
      },
    },
    tooltip: {
      showMarkers: false,
      formatter: (datum: any) => ({
        name: 'Responses',
        value: `${datum.responses} (${datum.level})`,
      }),
    },
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
  };

  const barConfig = {
    data: chartData,
    xField: 'date',
    yField: 'responses',
    seriesField: 'level',
    color: ({ level }: any) => {
      const colors = {
        critical: '#ff4d4f',
        warning: '#fa8c16',
        low: '#1890ff',
        acceptable: '#52c41a',
      };
      return colors[level as keyof typeof colors] || '#1890ff';
    },
    tooltip: {
      formatter: (datum: any) => ({
        name: 'Responses',
        value: `${datum.responses} (${datum.level})`,
      }),
    },
  };

  return (
    <div>
      {/* Controls */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <div>
              <Text strong>Select Brand:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="Choose a brand to view history"
                onChange={setSelectedBrandId}
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {brands?.map(brand => (
                  <Option key={brand.id} value={brand.id}>
                    {brand.name} ({brand.organisationName})
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <Text strong>Chart Type:</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={chartType}
                onChange={setChartType}
              >
                <Option value="line">
                  <LineChartOutlined /> Line Chart
                </Option>
                <Option value="bar">
                  <BarChartOutlined /> Bar Chart
                </Option>
              </Select>
            </div>
          </Col>
          <Col span={8}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={refetch}>
                Refresh
              </Button>
              <Button icon={<DownloadOutlined />}>
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Overall Statistics */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Checks"
                value={statistics.totalChecks}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Critical Issues"
                value={statistics.criticalCount}
                valueStyle={{ color: '#cf1322' }}
                suffix={`/ ${statistics.totalChecks}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Warnings"
                value={statistics.warningCount}
                valueStyle={{ color: '#fa8c16' }}
                suffix={`/ ${statistics.totalChecks}`}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Success Rate"
                value={Math.round((statistics.acceptableCount / statistics.totalChecks) * 100)}
                valueStyle={{ color: '#52c41a' }}
                suffix="%"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Chart and Table */}
      {selectedBrandId ? (
        <Spin spinning={historyLoading}>
          {brandHistory && brandHistory.length > 0 ? (
            <>
              {/* Chart */}
              <Card title="Response Trend" style={{ marginBottom: 16 }}>
                <div style={{
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f5f5f5',
                  border: '2px dashed #d9d9d9'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <BarChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                    <div style={{ marginTop: 16, color: '#999' }}>
                      Chart visualization will be available when @ant-design/plots is installed
                    </div>
                  </div>
                </div>
              </Card>

              {/* History Table */}
              <Card title="Monitoring History">
                <Table
                  columns={columns}
                  dataSource={brandHistory}
                  rowKey="id"
                  pagination={{ pageSize: 50 }}
                  size="small"
                />
              </Card>
            </>
          ) : (
            <Card>
              <Empty
                description="No monitoring history available for this brand"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          )}
        </Spin>
      ) : (
        <Card>
          <Empty
            description="Please select a brand to view monitoring history"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}
    </div>
  );
};